/**
 * The tone vocabulary — the app's ONE status→colour axis (ADR 0020).
 *
 * Named verbatim after the `--status-*` CSS tokens so there is no translation
 * table between two names for the same colour; a translation table is how this
 * codebase ended up with ~16 rival status maps. Nothing outside this module
 * reads `--status-*` directly (design-tokens rule 12).
 *
 * Lives in `components/common/` rather than `shared/` (feature-shaped logic
 * modules) or a feature folder (a tone the whole app speaks cannot be owned by
 * one feature).
 */

/**
 * The axis, as data — so a gallery or a test can iterate every tone and stay
 * exhaustive for free rather than re-typing the five names.
 */
export const TONES = [
	'success',
	'error',
	'alert',
	'pending',
	'neutral',
] as const

/** `success · error · alert · pending · neutral` — see the module doc. */
export type Tone = (typeof TONES)[number]

/**
 * Tinted background + text, for tone rendered **in a container** (`Pill`).
 *
 * Written as whole class names, never interpolated — Tailwind scans source
 * literally, and a built-up `bg-status-${tone}` is dropped silently at exit 0
 * (GOTCHAS: "Tailwind drops an unresolvable utility silently").
 */
export const TONE_SURFACE: Record<Tone, string> = {
	success: 'bg-status-success-bg text-status-success',
	error: 'bg-status-error-bg text-status-error',
	alert: 'bg-status-alert-bg text-status-alert',
	pending: 'bg-status-pending-bg text-status-pending',
	neutral: 'bg-status-neutral-bg text-status-neutral',
}

/**
 * Text colour only, for tone-coloured surfaces with **no container** — a
 * confidence score, a bare "Running" / "Completed" job word. Without this the
 * only way to tone such a surface is a raw palette utility, which is the drift
 * this effort removes.
 */
export const TONE_TEXT: Record<Tone, string> = {
	success: 'text-status-success',
	error: 'text-status-error',
	alert: 'text-status-alert',
	pending: 'text-status-pending',
	neutral: 'text-status-neutral',
}

/**
 * Solid background, for tone rendered as **filled geometry in HTML** — the dot
 * beside a status label, the indicator of a health bar, a segmented meter.
 *
 * The sibling of {@link TONE_FILL}, which does the same job for SVG. Both exist
 * because a shape has no text to colour and no container to tint:
 * {@link TONE_SURFACE}'s `-bg` token is a 14%-alpha wash designed to sit behind
 * a label, and a dot or progress bar drawn in it is invisible against the track.
 *
 * `Pill` owns tone-in-a-container and is deliberately non-interactive (ADR 0020
 * §3), so an interactive surface that must still show case status — the case
 * detail's status dropdown — renders a plain label beside a dot from this map
 * rather than a `Pill`. Without it, the only option is a raw palette utility,
 * which is the fork ADR 0020 exists to remove.
 *
 * Pair it with {@link TONE_TEXT} on the label, never on its own — a dot or bar
 * is hidden from assistive tech, so the reading has to be a word somewhere
 * (colour alone is a WCAG 1.4.1 failure, ADR 0020 §3).
 */
export const TONE_BG: Record<Tone, string> = {
	success: 'bg-status-success',
	error: 'bg-status-error',
	alert: 'bg-status-alert',
	pending: 'bg-status-pending',
	neutral: 'bg-status-neutral',
}

/**
 * SVG fill, for tone rendered as **chart geometry** — a donut slice, a bar
 * segment, a legend swatch.
 *
 * A chart that slices a domain this app already has pill colours for must read
 * those colours, or the same word is two colours on one screen: benign is a
 * green pill in the case table and a brand-blue wedge in the donut above it.
 * The charting library wants a colour per datum, so without this map the only
 * ways to get one are a raw palette utility or an inline `var(--status-*)` —
 * both of which fork the vocabulary this module exists to keep single
 * (design-tokens rule 12).
 *
 * The brand chart ramp (`--chart-1…5`) is still the right answer for a
 * breakdown with **no** good/bad meaning — integrations, tenants, rule names.
 * This map is for the ones that do.
 */
export const TONE_FILL: Record<Tone, string> = {
	success: 'fill-status-success',
	error: 'fill-status-error',
	alert: 'fill-status-alert',
	pending: 'fill-status-pending',
	neutral: 'fill-status-neutral',
}
